import { notifyApiError } from "./errorNotify";
const hostname =
  typeof window !== "undefined" && window.location
    ? window.location.hostname
    : "";

const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";
const isSightflightDesigner = hostname === "designer.sightflight.io";
const isEmpowerbitsDesigner = hostname === "designer.sf.empowerbits.com";
let apiUrl = "";

if (isLocalHost || isSightflightDesigner) {
  apiUrl = "https://app.sightflight.io/v1";
} else if (isEmpowerbitsDesigner) {
  apiUrl = "https://app.sf.empowerbits.com/v1";
} else {
  throw new Error(`Unsupported hostname for API mapping: ${hostname}`);
}

export const API_URL = apiUrl;

export const DEV_MOD = process.env.REACT_APP_DEV_MODE === "true";
export const LAT = "41.33769948652653";
export const LNG = "-73.04235403057667";
export const ADDRESS = "127 Ford St, Ansonia, CT 06401, USA";
const DEFAULT_GOOGLE_STATIC_KEY = "AIzaSyAo1viD-Ut0TzXTyihevwuf-9tv_J3dPa0";

export const GOOGLE_MAPS_KEY = (
  process.env.REACT_APP_GOOGLE_MAPS_KEY || ""
)
  .trim()
  .replace(/^['"]|['"]$/g, "");
export const GOOGLE_MAPS_MAP_ID = (
  process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || ""
)
  .trim()
  .replace(/^['"]|['"]$/g, "");
export const GOOGLE_SOLAR_API_KEY = (
  process.env.REACT_APP_GOOGLE_SOLAR_API_KEY ||
  process.env.GOOGLE_SOLAR_API_KEY ||
  "AIzaSyBh_Sgyc-N0zajP0G1uahHRi-47pjOm5cI" ||
  ""
)
  .trim()
  .replace(/^['"]|['"]$/g, "");
export const GOOGLE_MAPS_STATIC_KEY = (
  process.env.REACT_APP_GOOGLE_MAPS_STATIC_KEY ||
  GOOGLE_MAPS_KEY ||
  DEFAULT_GOOGLE_STATIC_KEY
)
  .trim()
  .replace(/^['"]|['"]$/g, "");
export const GOOGLE_SOLAR_DEFAULT_QUALITY = "HIGH";

export const MAP_TILE_ZOOM = 19;
export const MAP_TILE_SIZE = 640;
export const MAP_TILE_SCALE = 2;
export const WEB_MERCATOR_TILE_SIZE = 256;
export const MAPBOX_STYLE = (
  process.env.REACT_APP_MAPBOX_STATIC_STYLE || "mapbox/satellite-v9"
)
  .trim()
  .replace(/^['"]|['"]$/g, "");
const DEFAULT_MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoiYWZmYW5hbndhciIsImEiOiJjbWtmNDAxencwMHNlM2NzZTBhcGp3MGRkIn0.UN8-XCNzcogbCvW9qdfS_A";
export const MAPBOX_ACCESS_TOKEN = (
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || DEFAULT_MAPBOX_ACCESS_TOKEN
)
  .trim()
  .replace(/^['"]|['"]$/g, "") || DEFAULT_MAPBOX_ACCESS_TOKEN;

const metersToMapboxLatitude = (meters) => meters / 110540;
const metersToMapboxLongitude = (meters, latitude) =>
  meters / Math.max(1e-6, 111320 * Math.cos((latitude * Math.PI) / 180));

// Fit the raster to the LiDAR footprint, matching the sister app's map-tile
// coverage instead of using a centre/zoom crop.
export function buildMapboxBBoxURL({
  centerLat,
  centerLng,
  lidarMinX,
  lidarMaxX,
  lidarMinY,
  lidarMaxY,
  size = MAP_TILE_SIZE,
  retina = true,
} = {}) {
  const numeric = [centerLat, centerLng, lidarMinX, lidarMaxX, lidarMinY, lidarMaxY].map(Number);
  if (!MAPBOX_ACCESS_TOKEN || !numeric.every(Number.isFinite)) return "";
  const [lat, lng, minX, maxX, minY, maxY] = numeric;

  const mapCenterLat = lat + metersToMapboxLatitude((minY + maxY) / 2);
  const mapCenterLng = lng + metersToMapboxLongitude((minX + maxX) / 2, lat);
  const longitudeRadius = metersToMapboxLongitude((maxX - minX) / 2, mapCenterLat);
  const latitudeRadius = metersToMapboxLatitude((maxY - minY) / 2);
  const staticSize = Math.min(640, Math.max(64, Math.round(size)));

  return `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE}/static/` +
    `[${mapCenterLng - longitudeRadius},${mapCenterLat - latitudeRadius},${mapCenterLng + longitudeRadius},${mapCenterLat + latitudeRadius}]/` +
    `${staticSize}x${staticSize}${retina ? "@2x" : ""}?padding=0&access_token=${encodeURIComponent(MAPBOX_ACCESS_TOKEN)}`;
}

export const MODEL_URL = `https://maps.googleapis.com/maps/api/staticmap?center=${LAT},${LNG}&size=600x600&scale=2&zoom=19&maptype=satellite&key=${GOOGLE_MAPS_STATIC_KEY}`;


// Turn any API error into a friendly toast. Unlike the previous version, this
// no longer silently ignores errors that lack an HTTP response (timeouts /
// offline) — those are exactly the "the server isn't reachable" cases users
// most need to be told about.
export const handleErrors = (err, fallback = "Request failed. Please try again.") => {
  notifyApiError(err, { fallback });
};

export function convertDMSToDD(dmsLat, latRef, dmsLng, lngRef) {
  // Helper function to parse fraction strings (e.g., "41/1" or "266456/10000")
  const parseFraction = (fraction) => {
    const [numerator, denominator] = fraction.split('/').map(Number);
    return denominator ? numerator / denominator : NaN;
  };

  // Helper function to parse DMS string format (e.g., "43 deg 6' 50.10\"")
  const parseDMSString = (dmsString) => {
    const match = dmsString.match(/(\d+)\s*deg\s*(\d+)'?\s*([\d.]+)"\s*([NSEW])/i);
    if (match) {
      const [, degrees, minutes, seconds, ref] = match;
      return {
        dms: [Number(degrees), Number(minutes), Number(seconds)],
        ref: ref.toUpperCase(), // Normalize reference to uppercase
      };
    }
    console.error('Invalid DMS string format:', dmsString);
    return { dms: [NaN, NaN, NaN], ref: null };
  };

  // Function to convert DMS to decimal degrees
  const dmsToDecimal = (dms, ref) => {
    let degrees, minutes, seconds;

    if (Array.isArray(dms)) {
      // Handle fraction-based arrays
      degrees = parseFraction(dms[0]);
      minutes = parseFraction(dms[1]) / 60;
      seconds = parseFraction(dms[2]) / 3600;
    } else if (typeof dms === 'string') {
      // Handle string-based DMS
      const dmsf = parseDMSString(dms);
      [degrees, minutes, seconds] = dmsf.dms;
      ref = dmsf.ref;
      minutes /= 60;
      seconds /= 3600;
    } else {
      console.error('Invalid DMS format:', dms);
      return NaN;
    }

    // Compute the decimal degree
    let decimal = degrees + minutes + seconds;

    // Apply the correct sign based on the reference (N/S, E/W)
    if (ref === 'S' || ref === 'W') {
      decimal *= -1;
    }

    return decimal;
  };

  // Convert latitude and longitude
  const lat = dmsToDecimal(dmsLat, latRef);
  const lng = dmsToDecimal(dmsLng, lngRef);

  // Return the converted latitude and longitude
  if (isNaN(lat) || isNaN(lng)) {
    console.error('Conversion failed for coordinates:', dmsLat, latRef, dmsLng, lngRef);
    return null;
  }

  return { latitude: lat, longitude: lng };
}

export const bytesToSize = (input) => {
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const kibibyte = 1024;
  const mebibyte = kibibyte * 1024;
  const gibibyte = mebibyte * 1024;

  // Parse string input like "6.8 MiB"
  const parseFileSizeString = (fileSizeString) => {
    const match = fileSizeString.match(/^([\d.]+)\s*(B|KB|MB|GB|TB|PB|KiB|MiB|GiB|TiB|PiB)$/i);
    if (!match) {
      console.error("Invalid file size format:", fileSizeString);
      return NaN;
    }

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    // Convert the value to bytes
    switch (unit) {
      case "B":
        return value;
      case "KB":
      case "KIB":
        return value * kibibyte;
      case "MB":
      case "MIB":
        return value * mebibyte;
      case "GB":
      case "GIB":
        return value * gibibyte;
      default:
        console.error("Unsupported unit:", unit);
        return NaN;
    }
  };

  // If input is a string, parse it
  let bytes = typeof input === "string" ? parseFileSizeString(input) : input;

  // Handle invalid input
  if (isNaN(bytes)) {
    return "Invalid file size";
  }

  // Format the bytes to a readable size
  let i = 0;
  while (bytes >= kibibyte && i < units.length - 1) {
    bytes /= kibibyte;
    i++;
  }

  return bytes.toFixed(2) + " " + units[i];
};

//download image url and filenamee to download image
export const downloadImage = (blob, fileName) => {
  const link = document.createElement("a");
  link.href = blob;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const MAX_MERCATOR_LAT = 85.05112878;
const EARTH_RADIUS_METERS = 6378137;

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const clampLatitude = (latitude) =>
  Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, latitude));

const normalizeLongitude = (longitude) => {
  let normalized = longitude;
  while (normalized > 180) normalized -= 360;
  while (normalized < -180) normalized += 360;
  return normalized;
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;
const toDegrees = (radians) => (radians * 180) / Math.PI;

const projectLngToWorld = (longitude, worldSize) =>
  ((normalizeLongitude(longitude) + 180) / 360) * worldSize;

const projectLatToWorld = (latitude, worldSize) => {
  const clampedLat = clampLatitude(latitude);
  const latRad = toRadians(clampedLat);
  const mercator =
    0.5 -
    Math.log((1 + Math.sin(latRad)) / (1 - Math.sin(latRad))) / (4 * Math.PI);
  return mercator * worldSize;
};

const unprojectWorldToLng = (x, worldSize) => {
  const wrappedX = ((x % worldSize) + worldSize) % worldSize;
  return normalizeLongitude((wrappedX / worldSize) * 360 - 180);
};

const unprojectWorldToLat = (y, worldSize) => {
  const clampedY = Math.max(0, Math.min(worldSize, y));
  const mercator = 0.5 - clampedY / worldSize;
  const latRad = 2 * Math.atan(Math.exp(mercator * 2 * Math.PI)) - Math.PI / 2;
  return clampLatitude(toDegrees(latRad));
};

const haversineDistanceMeters = (lat1, lng1, lat2, lng2) => {
  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const dPhi = toRadians(lat2 - lat1);
  const dLambda = toRadians(lng2 - lng1);
  const a =
    Math.sin(dPhi / 2) * Math.sin(dPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) *
      Math.sin(dLambda / 2) * Math.sin(dLambda / 2);
  return 2 * EARTH_RADIUS_METERS * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const estimateMapStaticCoverageMeters = (
  mapReference,
  fallbackSizeMeters = 130
) => {
  const fallback = {
    mode: "fallback",
    widthMeters: fallbackSizeMeters,
    heightMeters: fallbackSizeMeters,
    bounds: null,
    center: null,
  };

  if (!mapReference || typeof mapReference !== "object") return fallback;

  let bounds = null;
  let centerLat = null;
  let centerLng = null;

  if (mapReference.mode === "geotiffAffine") {
    const westRaw = toFiniteNumber(mapReference.west);
    const eastRaw = toFiniteNumber(mapReference.east);
    const southRaw = toFiniteNumber(mapReference.south);
    const northRaw = toFiniteNumber(mapReference.north);

    if (westRaw != null && eastRaw != null && southRaw != null && northRaw != null) {
      const south = Math.min(southRaw, northRaw);
      const north = Math.max(southRaw, northRaw);

      bounds = {
        west: normalizeLongitude(westRaw),
        east: normalizeLongitude(eastRaw),
        south: clampLatitude(south),
        north: clampLatitude(north),
      };

      let deltaLng = bounds.east - bounds.west;
      while (deltaLng < 0) deltaLng += 360;
      while (deltaLng >= 360) deltaLng -= 360;

      centerLat = clampLatitude((bounds.south + bounds.north) / 2);
      centerLng = normalizeLongitude(bounds.west + deltaLng / 2);
    }
  } else if (mapReference.mode === "centerZoom") {
    const zoom = toFiniteNumber(mapReference.zoom);
    const width = toFiniteNumber(mapReference.width);
    const height = toFiniteNumber(mapReference.height);
    const rawCenterLat = toFiniteNumber(mapReference.centerLat);
    const rawCenterLng = toFiniteNumber(mapReference.centerLng);

    if (
      zoom != null &&
      width != null &&
      height != null &&
      rawCenterLat != null &&
      rawCenterLng != null
    ) {
      const worldSize = WEB_MERCATOR_TILE_SIZE * Math.pow(2, zoom);
      const centerX = projectLngToWorld(rawCenterLng, worldSize);
      const centerY = projectLatToWorld(rawCenterLat, worldSize);
      const halfWidth = width / 2;
      const halfHeight = height / 2;

      bounds = {
        west: unprojectWorldToLng(centerX - halfWidth, worldSize),
        east: unprojectWorldToLng(centerX + halfWidth, worldSize),
        north: unprojectWorldToLat(centerY - halfHeight, worldSize),
        south: unprojectWorldToLat(centerY + halfHeight, worldSize),
      };
      centerLat = clampLatitude(rawCenterLat);
      centerLng = normalizeLongitude(rawCenterLng);
    }
  } else if (mapReference.mode === "bbox") {
    const west = toFiniteNumber(mapReference.west);
    const east = toFiniteNumber(mapReference.east);
    const southRaw = toFiniteNumber(mapReference.south);
    const northRaw = toFiniteNumber(mapReference.north);

    if (west != null && east != null && southRaw != null && northRaw != null) {
      const south = Math.min(southRaw, northRaw);
      const north = Math.max(southRaw, northRaw);
      let deltaLng = east - west;
      while (deltaLng < 0) deltaLng += 360;
      while (deltaLng >= 360) deltaLng -= 360;

      bounds = {
        west: normalizeLongitude(west),
        east: normalizeLongitude(east),
        south: clampLatitude(south),
        north: clampLatitude(north),
      };

      centerLat = clampLatitude((south + north) / 2);
      centerLng = normalizeLongitude(west + deltaLng / 2);
    }
  }

  if (!bounds || centerLat == null || centerLng == null) return fallback;

  const widthMeters = haversineDistanceMeters(
    centerLat,
    bounds.west,
    centerLat,
    bounds.east
  );
  const heightMeters = haversineDistanceMeters(
    bounds.south,
    centerLng,
    bounds.north,
    centerLng
  );

  if (
    !Number.isFinite(widthMeters) ||
    widthMeters <= 0 ||
    !Number.isFinite(heightMeters) ||
    heightMeters <= 0
  ) {
    return fallback;
  }

  return {
    mode: mapReference.mode,
    widthMeters,
    heightMeters,
    bounds,
    center: { lat: centerLat, lng: centerLng },
  };
};

export const parseMapTileReference = (url, fallback = null) => {
  if (!url || typeof url !== "string") return fallback;

  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname || "";
    const path = parsedUrl.pathname || "";

    if (host.includes("googleapis.com") && path.includes("/maps/api/staticmap")) {
      const center = (parsedUrl.searchParams.get("center") || "")
        .split(",")
        .map((value) => toFiniteNumber(value.trim()));
      const centerLat = center.length === 2 ? center[0] : null;
      const centerLng = center.length === 2 ? center[1] : null;
      const zoom = toFiniteNumber(parsedUrl.searchParams.get("zoom"));

      const size = (parsedUrl.searchParams.get("size") || "")
        .toLowerCase()
        .match(/^(\d+)x(\d+)$/);
      const width = size ? toFiniteNumber(size[1]) : null;
      const height = size ? toFiniteNumber(size[2]) : null;
      const scale = toFiniteNumber(parsedUrl.searchParams.get("scale")) || 1;

      if (
        centerLat != null &&
        centerLng != null &&
        zoom != null &&
        width != null &&
        height != null
      ) {
        return {
          mode: "centerZoom",
          centerLat,
          centerLng,
          zoom,
          bearing: 0,
          pitch: 0,
          width,
          height,
          retina: scale > 1,
          scale,
          provider: "google-static",
          sourceUrl: url,
        };
      }
    }
  } catch (error) {
    return fallback;
  }

  return fallback;
};

export function buildMapTileURL({
  centerLat,
  centerLng,
  zoom = MAP_TILE_ZOOM,
  size = MAP_TILE_SIZE,
  width,
  height,
  markers,
  scale = MAP_TILE_SCALE,
} = {}) {
  const lat = toFiniteNumber(centerLat);
  const lng = toFiniteNumber(centerLng);
  const normalizedZoom = toFiniteNumber(zoom);
  const normalizedSize = toFiniteNumber(size);
  const normalizedWidth = toFiniteNumber(width);
  const normalizedHeight = toFiniteNumber(height);
  const normalizedScale = toFiniteNumber(scale) || 1;
  const requestedWidth = normalizedWidth ?? normalizedSize;
  const requestedHeight = normalizedHeight ?? normalizedSize;
  if (
    lat == null ||
    lng == null ||
    normalizedZoom == null ||
    requestedWidth == null ||
    requestedHeight == null ||
    requestedWidth <= 0 ||
    requestedHeight <= 0 ||
    !GOOGLE_MAPS_STATIC_KEY
  ) {
    return "";
  }

  const staticWidth = Math.min(640, Math.max(64, Math.round(requestedWidth)));
  const staticHeight = Math.min(640, Math.max(64, Math.round(requestedHeight)));
  const staticScale = Math.max(1, Math.min(2, Math.round(normalizedScale)));
  const markerParams = Array.isArray(markers)
    ? markers
        .map((marker) => {
          const markerLat = toFiniteNumber(marker?.lat);
          const markerLng = toFiniteNumber(marker?.lng);
          if (markerLat == null || markerLng == null) return "";
          const parts = [];
          if (marker?.size) parts.push(`size:${marker.size}`);
          if (marker?.color) parts.push(`color:${marker.color}`);
          if (marker?.label) parts.push(`label:${String(marker.label).slice(0, 1)}`);
          parts.push(`${markerLat},${markerLng}`);
          return `&markers=${encodeURIComponent(parts.join("|"))}`;
        })
        .filter(Boolean)
        .join("")
    : "";
  return (
    `https://maps.googleapis.com/maps/api/staticmap?` +
    `center=${lat},${lng}` +
    `&zoom=${normalizedZoom}` +
    `&size=${staticWidth}x${staticHeight}` +
    `&scale=${staticScale}` +
    `&maptype=satellite` +
    `&format=jpg` +
    markerParams +
    `&key=${encodeURIComponent(GOOGLE_MAPS_STATIC_KEY)}`
  );
}
